import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MeetingMinutesData, PreviousAnalysis } from '@ai-meeting/shared'
import type { LanguageUpdateStatus, LiveMemoStatus } from '@/hooks/useProxySttRecording'
import { applyMemoPane, isMemoPaneEvent } from '@/utils/applyMemoPane'
import type { RealtimeFeatures } from '@/utils/realtimeFeatures'
import type { AssistModel } from '@/pages/Meetings/components/Copilot/assistModel'
import type { AssistRoute } from '@/pages/Meetings/components/Copilot/assist.types'
import type {
  NativeMeetingParticipant,
  NativeMeetingSession,
  NativeParticipantUpsertEvent,
  NativeSessionGapEvent,
  NativeSpeakerActivityEvent,
  NativeSessionStatusEvent,
  NativeSttEvent,
  NativeTranscriptEvent,
  SttProvider,
} from '@ai-meeting/shared'
import type { RealtimeToken } from '@soniox/client'
import type { Socket } from 'socket.io-client'
import {
  createNativeSession,
  createNativeTranscriptSocket,
  fetchNativeAudioAccess,
  stopNativeSession,
  type CreateNativeSessionPayload,
} from '@/services/native-transcription'
import {
  ParticipantAiStreamCoordinator,
  type ParticipantAiStreamContext,
  type ParticipantAiStreamEvent,
  type ParticipantAiTokenUpdate,
} from '@/services/participant-ai-stream'
import { GatewayAudioClient, type TeamsAudioParticipant } from '@/services/teams-gateway-audio'
import { NativeMeetingAudioArchive } from '@/audio/NativeMeetingAudioArchive'
import { nativeTranscriptToToken } from '@/utils/nativeTranscriptToToken'
import { proxyEventToTokens, type ProxySttEvent } from '@/utils/proxySttEventProcessor'
import { applyLiveTranscriptEvent, type LiveTranscriptTurn } from '@/utils/liveTranscriptTurns'
import { groupLiveTokensBySpeakerTurn } from '@/utils/speakerTurnOrder'
function nativeSttEventToProxyEvent(event: NativeSttEvent): ProxySttEvent {
  const teamsSpeaker = event.speakerId
  return {
    event: event.event,
    text: event.text,
    translation: event.translation,
    translation_turns: event.translationTurns?.map((turn) => ({
      text: turn.text,
      batch_id: turn.batchId,
      speaker: teamsSpeaker,
    })),
    tokens: event.tokens?.map((token) => ({
      text: token.text,
      language: token.language,
      speaker: teamsSpeaker,
    })),
    speaker: teamsSpeaker,
    speaker_name: event.speakerName,
    start_time: event.startTime,
    language: event.language,
    sourceLang: event.sourceLang,
    targetLang: event.targetLang,
    translation_latency_ms: event.translationLatencyMs,
  }
}

function nativeSttEventToTokens(event: NativeSttEvent): RealtimeToken[] {
  return proxyEventToTokens(nativeSttEventToProxyEvent(event)).finalTokens
}

function nativeSttEventToBatchTokens(event: NativeSttEvent): RealtimeToken[] {
  return proxyEventToTokens(nativeSttEventToProxyEvent(event)).batchTokens
}

/**
 * How long to wait for `meeting-minutes.result` before falling back to the REST endpoint. The
 * request runs over the meeting's whole transcript, so it is slow by nature; the caller has an
 * archived-transcript fallback, and blocking the save on this indefinitely would be worse.
 */
const MINUTES_TIMEOUT_MS = 120_000

/**
 * How often the browser tells the backend it is still watching the session.
 *
 * Well under the backend's 3-minute abandonment timeout, so a handful of beats
 * can be lost to a sleeping laptop or a network handover without the bot being
 * pulled out of a meeting the user is still in.
 */
const NATIVE_HEARTBEAT_INTERVAL_MS = 20_000

interface UseNativeTranscriptSessionOptions {
  targetLang: string
  sourceLang?: string
  mode?: 'one_way' | 'two_way'
  sttProvider?: SttProvider
  /** Backlog project this meeting belongs to; forwarded onto every participant's config. */
  projectKey?: string | null
  context?: ParticipantAiStreamContext | null
  /** Read when each participant's socket sends its config — see the coordinator option. */
  getMeetingElapsedMs?: () => number
  /** Server's answer about which clock the timestamps are on — see the coordinator option. */
  onTimelineAnchor?: (info: { seedMs: number; resumed: boolean }) => void
  /** Pane switches copied onto every participant `config`. */
  getRealtimeFeatures?: () => RealtimeFeatures
  onRealtimeFeaturesReady?: (event: { features?: unknown }) => void
  onRealtimeFeaturesUpdated?: (event: { features?: unknown }) => void
  onRealtimeFeaturesError?: (code: 'bad_request' | 'not_supported') => void
  /** Which assistant answers Copilot, for every participant socket's `config`. */
  getAssistModel?: () => AssistModel
  /** `assist.model.status` from any participant socket. */
  onAssistModelStatus?: (event: unknown) => void
  /** `assist.model.update.error` from any participant socket. Non-fatal. */
  onAssistModelError?: (code: 'bad_request' | 'not_supported') => void
  /** Which kinds of question the Copilot answers, for every participant socket. */
  getAssistRoutes?: () => AssistRoute[]
  /** `assist.routes.status` from any participant socket. */
  onAssistRoutesStatus?: (event: unknown) => void
  /** `assist.routes.update.error` from any participant socket. Non-fatal. */
  onAssistRoutesError?: (code: 'bad_request' | 'not_supported') => void
}

export function useNativeTranscriptSession(options?: UseNativeTranscriptSessionOptions) {
  const [session, setSession] = useState<NativeMeetingSession | null>(null)
  const [finalEvents, setFinalEvents] = useState<NativeTranscriptEvent[]>([])
  const [partialEvents, setPartialEvents] = useState<NativeTranscriptEvent[]>([])
  const [sttEvents, setSttEvents] = useState<NativeSttEvent[]>([])
  const [directFinalTokens, setDirectFinalTokens] = useState<RealtimeToken[]>([])
  const [directBatchTokens, setDirectBatchTokens] = useState<RealtimeToken[]>([])
  const [directPartialTokens, setDirectPartialTokens] = useState<Map<string, RealtimeToken[]>>(
    new Map()
  )
  // The MEETING's running summary, pushed by the AI service over whichever participant sockets are
  // open. Every payload is a full-replace snapshot of the same server-side state, so the duplicates
  // that arrive (one per socket) are simply applied in order.
  const [memo, setMemo] = useState<PreviousAnalysis | null>(null)
  const [memoStatus, setMemoStatus] = useState<LiveMemoStatus>('idle')
  const [memoError, setMemoError] = useState<string | null>(null)
  const [languageUpdateStatus, setLanguageUpdateStatus] = useState<LanguageUpdateStatus>('idle')
  const [languageUpdateError, setLanguageUpdateError] = useState<string | null>(null)
  // A live language change is one user action but N acknowledgments (one per participant socket).
  // The FIRST answer settles the request and the rest are ignored: they all carry the same command,
  // so a later one can only repeat what the UI already reported — and a mix of accept/reject is a
  // broken server state either way, not something a second opinion resolves.
  const languageUpdateSettledRef = useRef(true)
  const [isPaused, setIsPaused] = useState(false)
  // Read inside the gateway's audio callback, which closes over the state it was created with —
  // a frame arriving in the gap between clicking Pause and that render committing would still be
  // captured. The ref flips the instant `pause()` runs.
  const pausedRef = useRef(false)
  const [participants, setParticipants] = useState<NativeMeetingParticipant[]>([])
  const [activeSpeakerSessionIds, setActiveSpeakerSessionIds] = useState<Set<string>>(new Set())
  const [gap, setGap] = useState<NativeSessionGapEvent | null>(null)
  const [error, setError] = useState<Error | null>(null)
  // Settles the in-flight `requestMinutes` promise when `meeting-minutes.*` comes back. Only ever
  // one at a time — the caller asks once, at the end of the meeting.
  const minutesResolverRef = useRef<
    ((data: MeetingMinutesData | null, failure?: Error) => void) | null
  >(null)
  // Per-participant end-of-meeting cost, keyed by socket so a repeated report replaces rather than
  // double-counts. The meeting's bill is their SUM — every participant's stream is billed
  // separately (see `onCost`).
  const costByParticipantRef = useRef<Map<string, { currency: string; totalCostUsd: number }>>(
    new Map()
  )
  const [liveTurns, setLiveTurns] = useState<LiveTranscriptTurn[]>([])
  const socketRef = useRef<Socket | null>(null)
  const gatewayAudioRef = useRef<GatewayAudioClient | null>(null)
  // Deliberately NOT torn down by `disconnect()`: the caller stops the session
  // first and only then asks for the recording, so the encoder has to outlive
  // the gateway socket.
  const audioArchiveRef = useRef(new NativeMeetingAudioArchive())
  const participantAiRef = useRef<ParticipantAiStreamCoordinator | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const lastSequenceRef = useRef(0)
  const activeSpeakerSessionIdsRef = useRef<Set<string>>(new Set())
  const speakerExpiryTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearSpeakerActivity = useCallback(() => {
    for (const timer of speakerExpiryTimersRef.current.values()) clearTimeout(timer)
    speakerExpiryTimersRef.current.clear()
    activeSpeakerSessionIdsRef.current = new Set()
    setActiveSpeakerSessionIds(new Set())
  }, [])

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== null) clearInterval(heartbeatTimerRef.current)
    heartbeatTimerRef.current = null
  }, [])

  const disconnect = useCallback(() => {
    stopHeartbeat()
    socketRef.current?.disconnect()
    socketRef.current = null
    gatewayAudioRef.current?.close()
    gatewayAudioRef.current = null
    participantAiRef.current?.close()
    participantAiRef.current = null
    clearSpeakerActivity()
  }, [clearSpeakerActivity, stopHeartbeat])

  /**
   * Drops the local connections and clears the session so the UI stops
   * presenting the meeting as live.
   *
   * Used when the browser loses network. `disconnect` alone is not enough:
   * `session` is what the screen derives "still running" from, so leaving it set
   * kept the Pause button showing and the user believing the meeting could carry
   * on. This deliberately does NOT tell the backend to stop the session: with
   * no network that call cannot land, so the bot stays in the call until the
   * backend's abandonment sweep reclaims it.
   */
  const dropConnection = useCallback(() => {
    disconnect()
    setSession(null)
  }, [disconnect])

  /** Releases resources owned by this document without deciding that the meeting ended. */
  const releaseClientResources = useCallback(() => {
    if (heartbeatTimerRef.current !== null) clearInterval(heartbeatTimerRef.current)
    heartbeatTimerRef.current = null
    socketRef.current?.disconnect()
    socketRef.current = null
    gatewayAudioRef.current?.close()
    gatewayAudioRef.current = null
    participantAiRef.current?.close()
    participantAiRef.current = null
    void audioArchiveRef.current.stop()
    for (const timer of speakerExpiryTimersRef.current.values()) clearTimeout(timer)
    speakerExpiryTimersRef.current.clear()

    sessionIdRef.current = null
  }, [])

  // Do not react to `pagehide`: Teams emits it as soon as the native Leave
  // confirmation opens, before the user chooses Leave or Cancel. Treating that
  // event as confirmation pulled the bot out of a meeting the user still occupied
  // (BRAI-447). A real owner departure is authoritative in the gateway roster and
  // the backend stops that owner's bot; the heartbeat timeout remains the crash /
  // closed-tab fallback. React unmount only releases this document's resources.
  useEffect(() => {
    return releaseClientResources
  }, [releaseClientResources])

  const bindSocket = useCallback((socket: Socket, sessionId: string) => {
    socket.on('connect', () => {
      socket.emit('session.subscribe', { sessionId, afterSequence: lastSequenceRef.current })
      // Tells the backend this browser is still watching. Without it a user who
      // closes the tab leaves the session CONNECTED and the bot never leaves the
      // call — nothing else moves `lastHeartbeatAt` in browser-audio mode, where
      // STT runs here rather than on the gateway. Re-armed on every `connect`
      // so a reconnect resumes beating (see
      // `NativeSessionsService.reconcileAbandonedTeamsSessions`).
      if (heartbeatTimerRef.current !== null) clearInterval(heartbeatTimerRef.current)
      socket.emit('session.heartbeat', { sessionId })
      heartbeatTimerRef.current = setInterval(() => {
        socket.emit('session.heartbeat', { sessionId })
      }, NATIVE_HEARTBEAT_INTERVAL_MS)
      heartbeatTimerRef.current.unref?.()
    })
    socket.on('transcript.partial', (event: NativeTranscriptEvent) => {
      if (event.sessionId !== sessionIdRef.current) return
      const key = `${event.speakerId ?? event.participantId ?? 'unknown'}:${event.startTimeMs}`
      setPartialEvents((current) => [
        ...current.filter(
          (item) =>
            `${item.speakerId ?? item.participantId ?? 'unknown'}:${item.startTimeMs}` !== key
        ),
        event,
      ])
    })
    socket.on('transcript.final', (event: NativeTranscriptEvent) => {
      if (event.sessionId !== sessionIdRef.current || event.sequence == null) return
      lastSequenceRef.current = Math.max(lastSequenceRef.current, event.sequence)
      setPartialEvents((current) =>
        current.filter(
          (item) =>
            !(
              (item.speakerId ?? item.participantId) === (event.speakerId ?? event.participantId) &&
              item.startTimeMs === event.startTimeMs
            )
        )
      )
      setFinalEvents((current) => {
        if (current.some((item) => item.sequence === event.sequence)) return current
        return [...current, event].sort(
          (left, right) => (left.sequence ?? 0) - (right.sequence ?? 0)
        )
      })
    })
    socket.on('stt.event', (event: NativeSttEvent) => {
      if (event.sessionId !== sessionIdRef.current) return
      if (participantAiRef.current) return
      setLiveTurns((current) =>
        applyLiveTranscriptEvent(current, nativeSttEventToProxyEvent(event))
      )
      setSttEvents((current) => {
        if (current.some((item) => item.providerEventId === event.providerEventId)) return current
        return [...current, event]
      })
    })
    socket.on('participant.upserted', (event: NativeParticipantUpsertEvent) => {
      if (event.sessionId !== sessionIdRef.current) return
      setParticipants((current) => {
        const index = current.findIndex(
          (participant) => participant.providerSessionId === event.participant.providerSessionId
        )
        if (index === -1) return [...current, event.participant]
        const next = [...current]
        next[index] = event.participant
        return next
      })
    })
    socket.on('speaker.activity', (event: NativeSpeakerActivityEvent) => {
      if (event.sessionId !== sessionIdRef.current) return
      setParticipants((current) => {
        if (
          current.some((participant) => participant.providerSessionId === event.providerSessionId)
        ) {
          return current
        }
        return [
          ...current,
          {
            id: `activity:${event.providerSessionId}`,
            providerParticipantId: event.providerParticipantId,
            providerSessionId: event.providerSessionId,
            displayName: event.displayName || 'Unknown speaker',
            kind: event.kind,
            joinedAt: null,
            leftAt: null,
          },
        ]
      })

      const providerSessionId = event.providerSessionId
      const previousTimer = speakerExpiryTimersRef.current.get(providerSessionId)
      if (previousTimer) clearTimeout(previousTimer)

      if (!activeSpeakerSessionIdsRef.current.has(providerSessionId)) {
        const next = new Set(activeSpeakerSessionIdsRef.current)
        next.add(providerSessionId)
        activeSpeakerSessionIdsRef.current = next
        setActiveSpeakerSessionIds(next)
      }

      const expiryTimer = setTimeout(() => {
        if (speakerExpiryTimersRef.current.get(providerSessionId) !== expiryTimer) return
        speakerExpiryTimersRef.current.delete(providerSessionId)
        const next = new Set(activeSpeakerSessionIdsRef.current)
        next.delete(providerSessionId)
        activeSpeakerSessionIdsRef.current = next
        setActiveSpeakerSessionIds(next)
      }, 1_500)
      speakerExpiryTimersRef.current.set(providerSessionId, expiryTimer)
    })
    socket.on('session.status', (event: NativeSessionStatusEvent) => {
      if (event.sessionId !== sessionIdRef.current) return
      setSession((current) =>
        current
          ? {
              ...current,
              status: event.status,
              consentStatus: event.consentStatus,
              failureCode: event.code,
              failureMessage: event.message,
            }
          : current
      )
    })
    socket.on('session.gap', (event: NativeSessionGapEvent) => {
      if (event.sessionId === sessionIdRef.current) setGap(event)
    })
    socket.on('connect_error', (cause: Error) => setError(cause))
  }, [])

  const start = useCallback(
    async (payload: CreateNativeSessionPayload) => {
      disconnect()
      setError(null)
      setGap(null)
      setFinalEvents([])
      setPartialEvents([])
      setSttEvents([])
      setDirectFinalTokens([])
      setDirectBatchTokens([])
      setDirectPartialTokens(new Map())
      setMemo(null)
      setMemoStatus('idle')
      setMemoError(null)
      setLanguageUpdateStatus('idle')
      setLanguageUpdateError(null)
      languageUpdateSettledRef.current = true
      costByParticipantRef.current = new Map()
      setLiveTurns([])
      setParticipants([])
      pausedRef.current = false
      setIsPaused(false)
      clearSpeakerActivity()
      // Drop any recording the previous session left unread — `stop()` detaches
      // synchronously, so the `start()` below always opens a fresh graph.
      void audioArchiveRef.current.stop()
      lastSequenceRef.current = 0
      const created = await createNativeSession(payload)
      sessionIdRef.current = created.id
      setSession(created)

      if (payload.provider === 'MICROSOFT_TEAMS') {
        try {
          const access = await fetchNativeAudioAccess(created.id)
          const onTokens = (
            participant: TeamsAudioParticipant,
            update: ParticipantAiTokenUpdate
          ) => {
            if (update.finalTokens.length > 0) {
              setDirectFinalTokens((current) => [...current, ...update.finalTokens])
            }
            if (update.batchTokens.length > 0) {
              setDirectBatchTokens((current) => [...current, ...update.batchTokens])
            }
            if (update.clearPartial || update.partialTokens.length > 0) {
              setDirectPartialTokens((current) => {
                const next = new Map(current)
                const partialStatus = update.partialStatus ?? 'original'
                const partialKey = `${participant.providerSessionId}:${partialStatus}`
                if (update.partialTokens.length > 0) {
                  next.set(partialKey, update.partialTokens)
                } else {
                  next.delete(partialKey)
                }
                return next
              })
            }
          }
          const onEvent = (
            _participant: TeamsAudioParticipant,
            event: ParticipantAiStreamEvent
          ) => {
            setLiveTurns((current) => applyLiveTranscriptEvent(current, event))
          }
          const coordinator = new ParticipantAiStreamCoordinator({
            meetingId: payload.meetingId,
            targetLang: options?.targetLang ?? 'vi',
            sourceLang: options?.sourceLang,
            mode: options?.mode,
            sttProvider: options?.sttProvider,
            projectKey: options?.projectKey,
            context: options?.context,
            getMeetingElapsedMs: options?.getMeetingElapsedMs,
            onTimelineAnchor: options?.onTimelineAnchor,
            getRealtimeFeatures: options?.getRealtimeFeatures,
            onRealtimeFeaturesReady: options?.onRealtimeFeaturesReady,
            onRealtimeFeaturesUpdated: options?.onRealtimeFeaturesUpdated,
            onRealtimeFeaturesError: options?.onRealtimeFeaturesError,
      getAssistModel: options?.getAssistModel,
      onAssistModelStatus: options?.onAssistModelStatus,
      onAssistModelError: options?.onAssistModelError,
      getAssistRoutes: options?.getAssistRoutes,
      onAssistRoutesStatus: options?.onAssistRoutesStatus,
      onAssistRoutesError: options?.onAssistRoutesError,
            onEvent,
            onTokens,
            onInsights: (event) => {
              if (event.event === 'summarize.processing') {
                setMemoStatus('loading')
                return
              }
              if (isMemoPaneEvent(event.event)) {
                // Deliberately not `?? null`, which is what this was: a frame arriving without a
                // body then erased the whole memo, and with three frames per cycle instead of one
                // there are three times as many chances for that to happen mid-meeting.
                setMemo((previous) => applyMemoPane(previous, event))
                setMemoStatus('success')
                setMemoError(null)
                return
              }
              if (event.event === 'summarize.error') {
                setMemoStatus('error')
                setMemoError(event.message ?? 'Live summary failed')
                return
              }
              if (event.event === 'meeting-minutes.result') {
                minutesResolverRef.current?.((event.data as MeetingMinutesData | undefined) ?? null)
                return
              }
              if (event.event === 'meeting-minutes.error') {
                minutesResolverRef.current?.(
                  null,
                  new Error(event.message ?? 'Meeting minutes failed')
                )
              }
            },
            onLanguageUpdate: (result) => {
              if (languageUpdateSettledRef.current) return
              languageUpdateSettledRef.current = true
              if (result.ok) {
                setLanguageUpdateError(null)
                setLanguageUpdateStatus('success')
                return
              }
              setLanguageUpdateError(result.message ?? null)
              setLanguageUpdateStatus('error')
            },
            onCost: (participant, currency, totalCostUsd) => {
              costByParticipantRef.current.set(participant.providerSessionId, {
                currency,
                totalCostUsd,
              })
            },
            onError: (cause) => setError(cause),
          })
          participantAiRef.current = coordinator
          audioArchiveRef.current.start()
          const gatewayAudio = new GatewayAudioClient({
            url: access.url,
            onAudio: (frame) => {
              // A paused meeting captures nothing at all: no transcript, and nothing in the
              // recording kept for playback either.
              if (pausedRef.current) return
              audioArchiveRef.current.write(frame)
              void coordinator
                .writeAudio(frame)
                .catch((cause: unknown) =>
                  setError(
                    cause instanceof Error
                      ? cause
                      : new Error('Failed to stream Teams participant audio')
                  )
                )
            },
            onParticipant: (participant) => {
              setParticipants((current) => {
                const nativeParticipant: NativeMeetingParticipant = {
                  id: `audio:${participant.providerSessionId}`,
                  providerParticipantId: participant.providerParticipantId,
                  providerSessionId: participant.providerSessionId,
                  displayName: participant.displayName || 'Unknown speaker',
                  kind: participant.kind,
                  joinedAt: null,
                  leftAt: null,
                }
                const index = current.findIndex(
                  (item) => item.providerSessionId === participant.providerSessionId
                )
                if (index === -1) return [...current, nativeParticipant]
                const next = [...current]
                next[index] = { ...current[index], ...nativeParticipant }
                return next
              })
            },
            onError: (cause) => setError(cause),
            onClose: () => {
              const activeCoordinator = participantAiRef.current
              participantAiRef.current = null
              void activeCoordinator
                ?.stop()
                .catch((cause: unknown) =>
                  setError(
                    cause instanceof Error
                      ? cause
                      : new Error('Failed to stop participant AI streams')
                  )
                )
            },
          })
          gatewayAudioRef.current = gatewayAudio
          gatewayAudio.connect()
        } catch (cause) {
          disconnect()
          let failure =
            cause instanceof Error ? cause : new Error('Failed to start Teams browser audio')
          let stopAcknowledgedByBackend = false
          try {
            await stopNativeSession(created.id)
            stopAcknowledgedByBackend = true
          } catch (stopCause) {
            const stopMessage =
              stopCause instanceof Error ? stopCause.message : 'unknown stop error'
            failure = new Error(
              `Teams audio setup failed and the native session could not be stopped: ${stopMessage}`
            )
          }
          sessionIdRef.current = null
          // Only clear `session` once the backend has actually acknowledged
          // the stop (moved the row to STOPPING) — create()'s own conflict
          // reconciliation (see native-sessions.service.ts) can then resolve
          // a retry from there. If the stop request itself never reached the
          // backend, the row may still be sitting in an earlier active
          // status (CREATED/STARTING/CONNECTED) that reconciliation doesn't
          // cover — leaving `session` set here deliberately keeps the Start
          // button disabled rather than let a retry race a second bot into
          // the same call with no way left to clean up the orphaned session.
          if (stopAcknowledgedByBackend) setSession(null)
          setError(failure)
          throw failure
        }
      }

      const socket = createNativeTranscriptSocket()
      socketRef.current = socket
      bindSocket(socket, created.id)
      socket.connect()
      return created
    },
    [bindSocket, clearSpeakerActivity, disconnect, options]
  )

  /**
   * Stop capturing the meeting until `resume()`, without giving up the call.
   *
   * Nothing is sent to the AI service and nothing is written to the recording, so the paused span
   * costs nothing and never reaches the transcript. The gateway socket and the bot's seat in the
   * call are deliberately kept — dropping them would need a fresh signed audio URL to come back,
   * and would pull the notetaker out of the meeting for what the user asked to be a pause.
   *
   * Drafts in flight are dropped rather than left frozen mid-word on screen; whatever the server
   * had already finalized stays, since that speech really did happen before the pause.
   */
  const pause = useCallback(() => {
    if (pausedRef.current) return
    pausedRef.current = true
    setIsPaused(true)
    participantAiRef.current?.setPaused(true)
    setPartialEvents([])
    setDirectPartialTokens(new Map())
  }, [])

  /** Resumes capture after `pause()`, telling the AI service before audio starts flowing again. */
  const resume = useCallback(() => {
    if (!pausedRef.current) return
    participantAiRef.current?.setPaused(false)
    pausedRef.current = false
    setIsPaused(false)
  }, [])

  const stop = useCallback(async () => {
    const sessionId = sessionIdRef.current
    if (!sessionId) return null
    let stopped: NativeMeetingSession | null = null
    let stopError: unknown
    try {
      stopped = await stopNativeSession(sessionId)
      setSession(stopped)
    } catch (error) {
      stopError = error
    }

    gatewayAudioRef.current?.close()
    gatewayAudioRef.current = null
    const coordinator = participantAiRef.current
    participantAiRef.current = null
    try {
      await coordinator?.stop()
    } catch (error) {
      if (stopError) {
        console.warn('Failed to flush participant AI streams after native stop failure', error)
      } else {
        stopError = error
      }
    } finally {
      disconnect()
    }

    if (stopError) throw stopError
    return stopped
  }, [disconnect])

  // Speech is grouped into speaker turns so two people talking at once no longer cut
  // each other's sentences into fragments. Each participant's still-streaming draft is
  // grouped with them rather than parked at the end of the pane: parking it there left
  // an unfinished sentence stranded behind whoever else was talking, and made the text
  // jump back into place the moment it finalized.
  const tokens = useMemo(
    () =>
      groupLiveTokensBySpeakerTurn(
        [
          ...finalEvents.map(nativeTranscriptToToken),
          ...sttEvents.flatMap(nativeSttEventToTokens),
          ...directFinalTokens,
        ],
        [
          ...partialEvents.map(nativeTranscriptToToken),
          ...[...directPartialTokens.values()].flat(),
        ]
      ),
    [directFinalTokens, directPartialTokens, finalEvents, partialEvents, sttEvents]
  )

  /**
   * Ask the meeting for its end-of-meeting minutes and resolve with the result.
   *
   * Mirrors `useProxySttRecording.finalizeMeetingMinutes` so the caller can treat both flows the
   * same way. Rejects when no participant socket can carry the request or the answer does not come
   * back in time — the caller's REST fallback then answers from the archived transcript.
   */
  const requestMinutes = useCallback(async (): Promise<MeetingMinutesData> => {
    const coordinator = participantAiRef.current
    if (!coordinator) throw new Error('No live participant AI stream to request minutes from')
    const requestId = `native-minutes-${Date.now()}`
    return await new Promise<MeetingMinutesData>((resolve, reject) => {
      const timer = setTimeout(() => {
        minutesResolverRef.current = null
        reject(new Error('Timed out waiting for meeting minutes'))
      }, MINUTES_TIMEOUT_MS)
      minutesResolverRef.current = (data, failure) => {
        clearTimeout(timer)
        minutesResolverRef.current = null
        if (failure) reject(failure)
        else resolve(data as MeetingMinutesData)
      }
      if (!coordinator.requestMinutes(requestId)) {
        clearTimeout(timer)
        minutesResolverRef.current = null
        reject(new Error('No open participant AI stream to request minutes from'))
      }
    })
  }, [])

  /**
   * Switch the language the meeting is translated into, without restarting anything (BRAI-359).
   *
   * The Teams flow has no `useProxySttRecording` socket of its own — translation happens on the
   * per-participant AI streams — so the picker in the meeting header has to come through here.
   *
   * @param nextTargetLang Language code to translate into from here on.
   * @returns `false` when there is no live session to update, so the caller can fall back to
   *   changing the language locally the way it does before a meeting starts.
   */
  const sendLanguageUpdate = useCallback((nextTargetLang: string): boolean => {
    const coordinator = participantAiRef.current
    if (!coordinator) return false

    setLanguageUpdateError(null)
    const notified = coordinator.setTargetLang(nextTargetLang)
    if (notified === 0) {
      // Nobody has spoken yet, so there is no socket to acknowledge anything. The coordinator has
      // the new language and every stream it opens from here carries it — reporting `pending` would
      // leave the picker disabled waiting for an answer that can never arrive.
      languageUpdateSettledRef.current = true
      setLanguageUpdateStatus('success')
      return true
    }

    languageUpdateSettledRef.current = false
    setLanguageUpdateStatus('pending')
    return true
  }, [])

  /**
   * Finalizes the browser-side recording of the meeting's gateway audio.
   *
   * Called after `stop()` — the bot leaving the call and the encoder flushing
   * are separate steps, so the archive is not torn down with the socket.
   *
   * @returns WebM/Opus recording ready for the meeting audio upload, or `null`
   *   when the session never carried browser audio (e.g. Google Meet).
   */
  const stopAudioArchive = useCallback((): Promise<Blob | null> => {
    return audioArchiveRef.current.stop()
  }, [])

  /**
   * Audio archived since the previous call, for the periodic upload that keeps
   * a dead tab from taking the whole recording with it (BRAI-457).
   *
   * @returns The new audio, or null when nothing has been archived since the
   *   previous call — including sessions that carry no browser audio at all.
   */
  const takeUnsentAudioChunks = useCallback((): Blob | null => {
    return audioArchiveRef.current.takeUnsentChunks()
  }, [])

  /** The meeting's total cost: every participant stream is billed on its own socket. */
  const readCostSummary = useCallback((): { currency: string; totalCostUsd: number } | null => {
    const reported = [...costByParticipantRef.current.values()]
    if (reported.length === 0) return null
    return {
      currency: reported[0].currency,
      totalCostUsd: reported.reduce((total, entry) => total + entry.totalCostUsd, 0),
    }
  }, [])

  // Publish the roster to the AI sockets as soon as the gateway names anyone, and again on every
  // change. The AI service builds the meeting's roster from each socket's `config.participant`, and
  // a socket only opens when its participant FIRST SPEAKS — so without this the summarizers spend
  // the opening minutes unable to name people the gateway already knows, and Confirm Action items
  // land anonymous only to gain a name several cycles later. Runs off `participants`, which is
  // populated by the activity/audio events well before the corresponding audio socket exists.
  useEffect(() => {
    const coordinator = participantAiRef.current
    if (!coordinator || participants.length === 0) return
    const roster: Record<string, string> = {}
    for (const participant of participants) {
      if (participant.providerParticipantId && participant.displayName) {
        roster[participant.providerParticipantId] = participant.displayName
      }
    }
    coordinator.setRoster(roster)
  }, [participants])

  const batchTokens = useMemo(
    () => [...sttEvents.flatMap(nativeSttEventToBatchTokens), ...directBatchTokens],
    [directBatchTokens, sttEvents]
  )

  return {
    session,
    tokens,
    memo,
    memoStatus,
    memoError,
    readCostSummary,
    batchTokens,
    liveTurns,
    participants,
    activeSpeakerSessionIds,
    gap,
    error,
    isPaused,
    languageUpdateStatus,
    languageUpdateError,
    sendLanguageUpdate,
    sendFeaturesUpdate: (features: Partial<RealtimeFeatures>) =>
      participantAiRef.current?.updateFeatures(features) ?? false,
    sendAssistModelUpdate: (model: AssistModel) =>
      participantAiRef.current?.updateAssistModel(model) ?? false,
    sendAssistRoutesUpdate: (routes: AssistRoute[]) =>
      participantAiRef.current?.updateAssistRoutes(routes) ?? false,
    start,
    pause,
    resume,
    stop,
    stopAudioArchive,
    takeUnsentAudioChunks,
    requestMinutes,
    disconnect,
    dropConnection,
  }
}
